import _slicedToArray from "/opt/build/repo/teleskop/node_modules/babel-preset-gatsby/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import { useCallback, useRef, useEffect, useState, useMemo } from 'react';
var canUseDOM = typeof window !== 'undefined';

function dethunkify(value) {
  return typeof value === 'function' ? value() : value;
}

function managedEventListener(target, type, callback, options) {
  target.addEventListener(type, callback, options);
  return function () {
    target.removeEventListener(type, callback, options);
  };
}

function managedInterval(callback, delayMs) {
  var id = setInterval(callback, delayMs);
  return function () {
    clearInterval(id);
  };
}

function useEventCallback(callback) {
  // Source: https://reactjs.org/docs/hooks-faq.html#how-to-read-an-often-changing-value-from-usecallback
  var ref = useRef();
  useEffect(function () {
    ref.current = callback;
  }, [callback]); // eslint-disable-next-line @typescript-eslint/no-non-null-assertion

  return useCallback(function () {
    return ref.current.apply(ref, arguments);
  }, [ref]);
}
/**
 * Tracks match state of a media query.
 *
 * @param query Media query to parse.
 *
 * @returns `true` if the associated media query list matches the state of the [`document`](https://developer.mozilla.org/docs/Web/API/Document), or `false` otherwise.
 *
 * @example
 * function Component() {
 *   const isWidescreen = useMedia('(min-aspect-ratio: 16/9)');
 *   // ...
 * }
 */


function useMedia(query) {
  var _useState = useState(function () {
    return canUseDOM ? matchMedia(query).matches : false;
  }),
      _useState2 = _slicedToArray(_useState, 2),
      matches = _useState2[0],
      setMatches = _useState2[1];

  useEffect(function () {
    var mediaQueryList = matchMedia(query);

    function handleChange() {
      setMatches(mediaQueryList.matches);
    } // Handle `query` param changes immediately


    handleChange(); // TODO: Refactor to `managedEventListener` when `change` event is supported

    mediaQueryList.addListener(handleChange);
    return function () {
      mediaQueryList.removeListener(handleChange);
    };
  }, [query]);
  return matches;
}
/**
 * Tracks color scheme preference of the user.
 *
 * @returns Preferred color scheme.
 *
 * @example
 * function Component() {
 *   const preferDarkMode = useColorSchemePreference() === 'dark';
 *   // ...
 * }
 */


function useColorSchemePreference() {
  var isDark = useMedia('(prefers-color-scheme: dark)');
  if (isDark) return 'dark';
  return 'light';
} // Source: https://w3c.github.io/deviceorientation/#dictdef-devicemotioneventinit


var initialState = {
  acceleration: null,
  accelerationIncludingGravity: null,
  rotationRate: null,
  interval: 0
};
/**
 * Tracks acceleration and rotation rate of the device.
 *
 * @returns Own properties of the last corresponding event.
 *
 * @example
 * function Component() {
 *   const { acceleration, rotationRate, interval } = useDeviceMotion();
 *   // ...
 * }
 */

function useDeviceMotion() {
  var _useState3 = useState(initialState),
      _useState4 = _slicedToArray(_useState3, 2),
      motion = _useState4[0],
      setMotion = _useState4[1]; // TODO: Request permission if necessary, see https://github.com/w3c/deviceorientation/issues/57


  useEffect(function () {
    return managedEventListener(window, 'devicemotion', function (event) {
      setMotion(event);
    });
  }, []);
  return motion;
} // Source: https://w3c.github.io/deviceorientation/#dictdef-deviceorientationeventinit


var initialState$1 = {
  alpha: null,
  beta: null,
  gamma: null,
  absolute: false
};
/**
 * Tracks physical orientation of the device.
 *
 * @returns Own properties of the last corresponding event.
 *
 * @example
 * function Component() {
 *   const { alpha, beta, gamma } = useDeviceOrientation();
 *   // ...
 * }
 */

function useDeviceOrientation() {
  var _useState5 = useState(initialState$1),
      _useState6 = _slicedToArray(_useState5, 2),
      orientation = _useState6[0],
      setOrientation = _useState6[1];

  useEffect(function () {
    return managedEventListener(window, 'deviceorientation', function (event) {
      setOrientation(event);
    });
  }, []);
  return orientation;
}
/**
 * Tracks loading state of the page.
 *
 * @returns Readiness of the [`document`](https://developer.mozilla.org/docs/Web/API/Document), which is `'loading'` by default.
 *
 * @example
 * function Component() {
 *   const documentReadiness = useDocumentReadiness();
 *   if (documentReadiness === 'interactive') {
 *     // You may interact with any element of the document from now
 *   }
 *   // ...
 * }
 */


function useDocumentReadiness() {
  var _useState7 = useState(canUseDOM ? document.readyState : 'loading'),
      _useState8 = _slicedToArray(_useState7, 2),
      readiness = _useState8[0],
      setReadiness = _useState8[1];

  useEffect(function () {
    return managedEventListener(document, 'readystatechange', function () {
      setReadiness(document.readyState);
    });
  }, []);
  return readiness;
}
/**
 * Tracks visibility of the page.
 *
 * @returns Visibility state of the [`document`](https://developer.mozilla.org/docs/Web/API/Document), which is `'visible'` by default.
 *
 * @example
 * function Component() {
 *   const documentVisibility = useDocumentVisibility();
 *   if (documentVisibility === 'hidden') {
 *     // Reduce resource utilization to aid background page performance
 *   }
 *   // ...
 * }
 */


function useDocumentVisibility() {
  var _useState9 = useState(canUseDOM ? document.visibilityState : 'visible'),
      _useState10 = _slicedToArray(_useState9, 2),
      visibility = _useState10[0],
      setVisibility = _useState10[1];

  useEffect(function () {
    return managedEventListener(document, 'visibilitychange', function () {
      setVisibility(document.visibilityState);
    });
  }, []);
  return visibility;
}
/**
 * Listens to an event while the enclosing component is mounted.
 *
 * @see [Event reference on MDN](https://developer.mozilla.org/en-US/docs/Web/Events)
 *
 * @param {EventTarget} target Target to listen on, possibly a DOM element or a remote service connector.
 * @param {string} type Name of event (case-sensitive).
 * @param {EventListener} callback Method to execute whenever the event fires.
 * @param options Additional listener characteristics.
 *
 * @example
 * function Component() {
 *   useEventListener(window, 'error', () => {
 *     console.log('A resource failed to load.');
 *   });
 *   // ...
 * }
 */


function useEventListener(target, type, callback, options) {
  // Based on the implementation of `useInterval`
  var savedCallback = useEventCallback(callback);
  useEffect(function () {
    return managedEventListener(target, type, savedCallback, options);
  }, [options, savedCallback, target, type]);
}
/**
 * Tracks focus state of an element.
 *
 * @returns Whether the element has focus, and props to be spread over the element under observation.
 *
 * @example
 * function Component() {
 *   const [isFocused, bindFocus] = useFocus();
 *   // ...
 *   return <ElementToObserve {...bindFocus} />;
 * }
 */


function useFocus() {
  var _useState11 = useState(false),
      _useState12 = _slicedToArray(_useState11, 2),
      isFocused = _useState12[0],
      setFocused = _useState12[1];

  return [isFocused, {
    onFocus: function onFocus() {
      setFocused(true);
    },
    onBlur: function onBlur() {
      setFocused(false);
    }
  }];
}
/**
 * Tracks geolocation of the device.
 *
 * @param options Additional watching options.
 * @param errorCallback Method to execute in case of an error, e.g. when the user denies location sharing permissions.
 * @returns Locational data, or `undefined` when unavailable.
 *
 * @example
 * function Component() {
 *   const geolocation = useGeolocation();
 *   if (geolocation) {
 *     const { coords } = geolocation;
 *   }
 *   // ...
 * }
 */


function useGeolocation(options, errorCallback) {
  var _useState13 = useState(),
      _useState14 = _slicedToArray(_useState13, 2),
      position = _useState14[0],
      setPosition = _useState14[1];

  useEffect(function () {
    var id = navigator.geolocation.watchPosition(setPosition, errorCallback, options);
    return function () {
      navigator.geolocation.clearWatch(id);
    };
  }, [errorCallback, options]);
  return position;
}
/**
 * Tracks hover state of an element.
 *
 * @param {boolean} disallowTouch Determines whether touch gestures should be ignored.
 * @returns Whether the element is hovered, and props to be spread over the element under observation.
 *
 * @example
 * function Component() {
 *   const [isHovered, bindHover] = useHover();
 *   // ...
 *   return <ElementToObserve {...bindHover} />;
 * }
 */


function useHover() {
  var disallowTouch = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;

  var _useState15 = useState(false),
      _useState16 = _slicedToArray(_useState15, 2),
      isHovered = _useState16[0],
      setHovered = _useState16[1];

  return [isHovered, {
    onMouseEnter: function onMouseEnter() {
      setHovered(true);
    },
    onMouseLeave: function onMouseLeave() {
      setHovered(false);
    },
    onTouchStart: function onTouchStart() {
      setHovered(!disallowTouch);
    },
    onTouchEnd: function onTouchEnd() {
      setHovered(false);
    }
  }];
}
/**
 * Repeatedly calls a function with a fixed time delay between each call.
 *
 * 📝 _Timings may be inherently inaccurate, due to the implementation of [`setInterval`](https://developer.mozilla.org/docs/Web/API/WindowOrWorkerGlobalScope/setInterval) under the hood._
 *
 * @param callback Method to execute periodically.
 * @param delayMs Time, in milliseconds, to wait between executions of the specified function. Set to `null` for pausing.
 *
 * @example
 * function Component() {
 *   useInterval(() => {
 *     // Custom logic to execute each second
 *   }, 1000);
 *   // ...
 * }
 */


function useInterval(callback, delayMs) {
  // Source: https://overreacted.io/making-setinterval-declarative-with-react-hooks/
  var savedCallback = useEventCallback(callback);
  useEffect(function () {
    return delayMs != null ? managedInterval(savedCallback, delayMs) : undefined;
  }, [delayMs, savedCallback]);
}

function getPreferredLanguages() {
  return navigator.languages || [navigator.language];
}
/**
 * Tracks language preferences of the user.
 *
 * @returns An array of [BCP 47](https://tools.ietf.org/html/bcp47) language tags, ordered by preference with the most preferred language first.
 *
 * @example
 * function Component() {
 *   const preferredLanguages = useLanguagePreferences();
 *   // ...
 * }
 */


function useLanguagePreferences() {
  var _useState17 = useState(canUseDOM ? getPreferredLanguages() : ['en-US', 'en']),
      _useState18 = _slicedToArray(_useState17, 2),
      languages = _useState18[0],
      setLanguages = _useState18[1];

  useEffect(function () {
    return managedEventListener(window, 'languagechange', function () {
      setLanguages(getPreferredLanguages());
    });
  }, []);
  return languages;
}

function useStorage(getStorage, key) {
  var initialValue = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
  var errorCallback = arguments.length > 3 ? arguments[3] : undefined;
  var storage = useMemo(function () {
    try {
      // Check if the storage object is defined and available
      // Prior to Firefox 70, localStorage may be null
      return getStorage(); // eslint-disable-next-line no-empty
    } catch (_unused) {}

    return null;
  }, [getStorage]);

  var _useState19 = useState(function () {
    var serializedValue = storage === null || storage === void 0 ? void 0 : storage.getItem(key);
    if (serializedValue == null) return dethunkify(initialValue);

    try {
      return JSON.parse(serializedValue);
    } catch (_unused2) {
      // Backwards compatibility with past stored non-serialized values
      return serializedValue;
    }
  }),
      _useState20 = _slicedToArray(_useState19, 2),
      value = _useState20[0],
      setValue = _useState20[1];

  useEffect(function () {
    if (storage) {
      try {
        storage.setItem(key, JSON.stringify(value));
      } catch (error) {
        errorCallback === null || errorCallback === void 0 ? void 0 : errorCallback(error);
      }
    }
  }, [errorCallback, key, storage, value]);
  return [value, setValue];
}

var getLocalStorage = function getLocalStorage() {
  return localStorage;
};
/**
 * Stores a key/value pair statefully in [`localStorage`](https://developer.mozilla.org/docs/Web/API/Window/localStorage).
 *
 * @see [`useState` hook](https://reactjs.org/docs/hooks-reference.html#usestate), which exposes a similar interface
 *
 * @param key Identifier to associate the stored value with.
 * @param initialValue Value used when no item exists with the given key. Lazy initialization is available by using a function which returns the desired value.
 * @param errorCallback Method to execute in case of an error, e.g. when the storage quota has been exceeded or trying to store a circular data structure.
 * @returns A statefully stored value, and a function to update it.
 *
 * @example
 * function Component() {
 *   const [visitCount, setVisitCount] = useLocalStorage<number>('visitCount', 0);
 *   useEffect(() => {
 *     setVisitCount(count => count + 1);
 *   }, []);
 *   // ...
 * }
 */


function useLocalStorage(key) {
  var initialValue = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  var errorCallback = arguments.length > 2 ? arguments[2] : undefined;
  return useStorage(getLocalStorage, key, initialValue, errorCallback);
}
/**
 * Tracks motion intensity preference of the user.
 *
 * @returns Preferred motion intensity.
 *
 * @example
 * function Component() {
 *   const preferReducedMotion = useMotionPreference() === 'reduce';
 *   // ...
 * }
 */


function useMotionPreference() {
  var isReduce = useMedia('(prefers-reduced-motion: reduce)');
  if (isReduce) return 'reduce';
  return 'no-preference';
}
/**
 * Tracks mouse position.
 *
 * @returns Coordinates `[x, y]`, falling back to `[0, 0]` when unavailable.
 *
 * @example
 * function Component() {
 *   const [mouseX, mouseY] = useMouseCoords();
 *   // ...
 * }
 */


function useMouseCoords() {
  var _useState21 = useState([0, 0]),
      _useState22 = _slicedToArray(_useState21, 2),
      coords = _useState22[0],
      setCoords = _useState22[1];

  useEffect(function () {
    return managedEventListener(window, 'mousemove', function (event) {
      setCoords([event.clientX, event.clientY]);
    });
  }, []);
  return coords;
}
/**
 * Tracks information about the network's availability.
 *
 * ⚠️ _This attribute is inherently unreliable. A computer can be connected to a network without having internet access._
 *
 * @returns `false` if the user agent is definitely offline, or `true` if it might be online.
 *
 * @example
 * function Component() {
 *   const isOnline = useNetworkAvailability();
 *   // ...
 * }
 */


function useNetworkAvailability() {
  var _useState23 = useState(canUseDOM ? navigator.onLine : true),
      _useState24 = _slicedToArray(_useState23, 2),
      online = _useState24[0],
      setOnline = _useState24[1];

  useEffect(function () {
    var cleanup1 = managedEventListener(window, 'offline', function () {
      setOnline(false);
    });
    var cleanup2 = managedEventListener(window, 'online', function () {
      setOnline(true);
    });
    return function () {
      cleanup1();
      cleanup2();
    };
  }, []);
  return online;
}
/**
 * Tracks information about the device's network connection.
 *
 * ⚗️ _The underlying technology is experimental. Please be aware about browser compatibility before using this in production._
 *
 * @returns Connection data, or `undefined` when unavailable.
 *
 * @example
 * function Component() {
 *   const networkInformation = useNetworkInformation();
 *   if (networkInformation) {
 *     const { effectiveType, downlink, rtt, saveData } = networkInformation;
 *   }
 *   // ...
 * }
 */


function useNetworkInformation() {
  var _useState25 = useState(canUseDOM ? navigator.connection : undefined),
      _useState26 = _slicedToArray(_useState25, 2),
      networkInformation = _useState26[0],
      setNetworkInformation = _useState26[1];

  useEffect(function () {
    return navigator.connection ? managedEventListener(navigator.connection, 'change', function () {
      setNetworkInformation(navigator.connection);
    }) : undefined;
  }, []);
  return networkInformation;
}

var getSessionStorage = function getSessionStorage() {
  return sessionStorage;
};
/**
 * Stores a key/value pair statefully in [`sessionStorage`](https://developer.mozilla.org/docs/Web/API/Window/sessionStorage).
 *
 * @see [`useState` hook](https://reactjs.org/docs/hooks-reference.html#usestate), which exposes a similar interface
 *
 * @param key Identifier to associate the stored value with.
 * @param initialValue Value used when no item exists with the given key. Lazy initialization is available by using a function which returns the desired value.
 * @param errorCallback Method to execute in case of an error, e.g. when the storage quota has been exceeded or trying to store a circular data structure.
 * @returns A statefully stored value, and a function to update it.
 *
 * @example
 * function Component() {
 *   const [name, setName] = useSessionStorage<string>('name', 'Anonymous');
 *   // ...
 * }
 */


function useSessionStorage(key) {
  var initialValue = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  var errorCallback = arguments.length > 2 ? arguments[2] : undefined;
  return useStorage(getSessionStorage, key, initialValue, errorCallback);
}
/**
 * Tracks size of an element.
 *
 * ⚗️ _The underlying technology is experimental. Please be aware about browser compatibility before using this in production._
 *
 * @param ref Attribute attached to the element under observation.
 * @param {TypeOf<ResizeObserver>} ResizeObserverOverride Replacement for `window.ResizeObserver`, e.g. [a polyfill](https://github.com/juggle/resize-observer).
 *
 * @returns Dimensions `[width, height]`, falling back to `[0, 0]` when unavailable.
 *
 * @example
 * function Component() {
 *   const ref = useRef<HTMLElement>(null);
 *   const [width, height] = useSize(ref);
 *   // ...
 *   return <ElementToObserve ref={ref} />;
 * }
 */


function useSize(ref, ResizeObserverOverride) {
  var _useState27 = useState([0, 0]),
      _useState28 = _slicedToArray(_useState27, 2),
      size = _useState28[0],
      setSize = _useState28[1];

  useEffect(function () {
    var ResizeObserver = ResizeObserverOverride || window.ResizeObserver;
    if (!ResizeObserver || !ref.current) return undefined;
    var observer = new ResizeObserver(function (_ref) {
      var _ref2 = _slicedToArray(_ref, 1),
          entry = _ref2[0];

      var _entry$contentRect = entry.contentRect,
          width = _entry$contentRect.width,
          height = _entry$contentRect.height;
      setSize([width, height]);
    });
    observer.observe(ref.current);
    return function () {
      observer.disconnect();
    };
  }, [ResizeObserverOverride, ref]);
  return size;
}
/**
 * Tracks visual viewport scale.
 *
 * ⚗️ _The underlying technology is experimental. Please be aware about browser compatibility before using this in production._
 *
 * @returns Pinch-zoom scaling factor, falling back to `0` when unavailable.
 *
 * @example
 * function Component() {
 *   const viewportScale = useViewportScale();
 *   // ...
 * }
 */


function useViewportScale() {
  var _useState29 = useState(canUseDOM ? window.visualViewport.scale : 0),
      _useState30 = _slicedToArray(_useState29, 2),
      scale = _useState30[0],
      setScale = _useState30[1];

  useEffect(function () {
    return managedEventListener(window.visualViewport, 'resize', function () {
      setScale(window.visualViewport.scale);
    });
  }, []);
  return scale;
}
/**
 * Tracks visual viewport scroll position.
 *
 * ⚗️ _The underlying technology is experimental. Please be aware about browser compatibility before using this in production._
 *
 * @returns Coordinates `[x, y]`, falling back to `[0, 0]` when unavailable.
 *
 * @example
 * function Component() {
 *   const [viewportScrollX, viewportScrollY] = useViewportScrollCoords();
 *   // ...
 * }
 */


function useViewportScrollCoords() {
  var _useState31 = useState(canUseDOM ? [window.visualViewport.pageLeft, window.visualViewport.pageTop] : [0, 0]),
      _useState32 = _slicedToArray(_useState31, 2),
      coords = _useState32[0],
      setCoords = _useState32[1];

  useEffect(function () {
    return managedEventListener(window.visualViewport, 'scroll', function () {
      setCoords([window.visualViewport.pageLeft, window.visualViewport.pageTop]);
    });
  }, []);
  return coords;
}
/**
 * Tracks visual viewport size.
 *
 * ⚗️ _The underlying technology is experimental. Please be aware about browser compatibility before using this in production._
 *
 * @returns Dimensions `[width, height]`, falling back to `[0, 0]` when unavailable.
 *
 * @example
 * function Component() {
 *   const [viewportWidth, viewportHeight] = useViewportSize();
 *   // ...
 * }
 */


function useViewportSize() {
  var _useState33 = useState(canUseDOM ? [window.visualViewport.width, window.visualViewport.height] : [0, 0]),
      _useState34 = _slicedToArray(_useState33, 2),
      size = _useState34[0],
      setSize = _useState34[1];

  useEffect(function () {
    return managedEventListener(window.visualViewport, 'resize', function () {
      setSize([window.visualViewport.width, window.visualViewport.height]);
    });
  }, []);
  return size;
}
/**
 * Tracks window scroll position.
 *
 * @returns Coordinates `[x, y]`, falling back to `[0, 0]` when unavailable.
 *
 * @example
 * function Component() {
 *   const [windowScrollX, windowScrollY] = useWindowScrollCoords();
 *   // ...
 * }
 */


function useWindowScrollCoords() {
  var _useState35 = useState(canUseDOM ? [window.pageXOffset, window.pageYOffset] : [0, 0]),
      _useState36 = _slicedToArray(_useState35, 2),
      coords = _useState36[0],
      setCoords = _useState36[1];

  useEffect(function () {
    return managedEventListener(window, 'scroll', function () {
      setCoords([window.pageXOffset, window.pageYOffset]);
    });
  }, []);
  return coords;
}
/**
 * Tracks window size.
 *
 * @returns Dimensions `[width, height]`, falling back to `[0, 0]` when unavailable.
 *
 * @example
 * function Component() {
 *   const [windowWidth, windowHeight] = useWindowSize();
 *   // ...
 * }
 */


function useWindowSize() {
  var _useState37 = useState(canUseDOM ? [window.innerWidth, window.innerHeight] : [0, 0]),
      _useState38 = _slicedToArray(_useState37, 2),
      size = _useState38[0],
      setSize = _useState38[1];

  useEffect(function () {
    return managedEventListener(window, 'resize', function () {
      setSize([window.innerWidth, window.innerHeight]);
    });
  }, []);
  return size;
}

export { useColorSchemePreference, useDeviceMotion, useDeviceOrientation, useDocumentReadiness, useDocumentVisibility, useEventListener, useFocus, useGeolocation, useHover, useInterval, useLanguagePreferences, useLocalStorage, useMedia, useMotionPreference, useMouseCoords, useNetworkAvailability, useNetworkInformation, useSessionStorage, useSize, useViewportScale, useViewportScrollCoords, useViewportSize, useWindowScrollCoords, useWindowSize };